import React, { useState, useEffect } from "react";
import { collection, getDocs, query, where, updateDoc, doc, getDoc, addDoc, deleteDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Plus, Search, X, FileText, TrendingUp, CheckCircle2, Clock } from "lucide-react";
import { ACTION_BTN_GOLD, ACTION_BTN_WHITE, ACTION_BTN_ICON } from "../styles/actionButtons";
import toast from "react-hot-toast";
import RaiseInvoiceForm from "../components/RaiseInvoiceForm";
import InvoiceList from "../components/InvoiceList";
import InvoiceSeriesDialog from "../components/InvoiceSeriesDialog";
import { syncInvoiceToClient, deleteSyncedInvoice } from "../services/invoiceSyncService";

import SelectMenu from "../components/crm/SelectMenu";
function Invoices() {
  const { globalUser } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  // const [isScheduleFormOpen, setIsScheduleFormOpen] = useState(false);
  const [isSeriesDialogOpen, setIsSeriesDialogOpen] = useState(false);
  const [currentSeries, setCurrentSeries] = useState("1");
  const [activeTab, setActiveTab] = useState("raised");
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [userName, setUserName] = useState("");

  // Check user type from localStorage
  const userType = localStorage.getItem("userType");
  const isClient = userType === "client";
  // Helper function to get current week's start (Monday) and end (Sunday)
  const getCurrentWeek = () => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

    // Calculate days to subtract to get to Monday
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    // Get Monday of current week
    const monday = new Date(today);
    monday.setDate(today.getDate() - daysToMonday);

    // Get Sunday of current week
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    // Format to YYYY-MM-DD without timezone issues
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      startDate: formatDate(monday),
      endDate: formatDate(today) // Use today instead of Sunday if week hasn't ended
    };
  };

  const [dateFilter, setDateFilter] = useState({
    type: "raisedOn",
    ...getCurrentWeek()
  });

  // Sort and Filter states
  const [sortConfig, setSortConfig] = useState({
    field: "raisedOn",
    direction: "desc"
  });
  const [showSortModal, setShowSortModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filters, setFilters] = useState([]);

  // Quick search/filter
  const [quickSearchText, setQuickSearchText] = useState("");
  const [activeQuickFilter, setActiveQuickFilter] = useState("all");

  // Metric states
  const [metrics, setMetrics] = useState({
    raised: 0,
    paid: 0,
    pending: 0,
    // scheduled: 0
  });

  useEffect(() => {
    if (isClient) {
      fetchClientSharedInvoices();
    } else {
      fetchUserName();
      fetchAllCases();
      fetchAllClients();
      fetchAllInvoices();
      fetchCurrentSeries();
    }
  }, [globalUser]);

  useEffect(() => {
    filterInvoicesByTab();
  }, [activeTab, invoices, dateFilter, sortConfig, filters, quickSearchText, activeQuickFilter]);

  const fetchCurrentSeries = async () => {
    if (!globalUser?.uid) return;

    try {
      const settingsRef = doc(db, "lawyers", globalUser.uid, "settings", "invoiceSeries");
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        const data = settingsSnap.data();
        const series = data.currentSeries || "1";
        console.log("=== Fetched Invoice Series ===");
        console.log("Series from database:", series);
        setCurrentSeries(series);
      } else {
        console.log("=== Invoice Series ===");
        console.log("No series found in database, using default: 1");
        setCurrentSeries("1");
      }
    } catch (error) {
      console.error("Error fetching invoice series:", error);
      setCurrentSeries("1");
    }
  };

  const fetchUserName = async () => {
    if (!globalUser?.uid) return;

    try {
      const userDocRef = doc(db, "lawyers", globalUser.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserName(userData.name || "User");
      } else {
        console.log("No user document found for UID:", globalUser.uid);
        setUserName("User");
      }
    } catch (error) {
      console.error("Error fetching user name:", error);
      setUserName("User");
    }
  };

  const fetchAllCases = async () => {
    if (!globalUser?.uid) return;

    try {
      const allCases = [];
      const collections = ["pending", "disposed"];

      for (const collectionName of collections) {
        const casesQuery = query(
          collection(db, collectionName),
          where("owner", "==", globalUser.uid)
        );
        const casesSnapshot = await getDocs(casesQuery);
        casesSnapshot.docs.forEach((doc) => {
          allCases.push({
            id: doc.id,
            collection: collectionName,
            caseNo: doc.data().caseNo,
            petitionerName: doc.data().petitionerName,
            respondentName: doc.data().respondentName,
            ...doc.data(),
          });
        });
      }

      setCases(allCases);
    } catch (error) {
      console.error("Error fetching cases:", error);
      toast.error("Failed to load cases");
    }
  };

  const fetchAllClients = async () => {
    if (!globalUser?.uid) return;

    try {
      const clientsQuery = query(
        collection(db, "clients"),
        where("owner", "==", globalUser.uid)
      );
      const clientsSnapshot = await getDocs(clientsQuery);
      const clientsList = clientsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setClients(clientsList);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const fetchAllInvoices = async () => {
    if (!globalUser?.uid) return;

    setLoading(true);
    try {
      const invoicesRef = collection(db, "lawyers", globalUser.uid, "invoices");
      const invoicesSnapshot = await getDocs(invoicesRef);

      const allInvoices = invoicesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      console.log("Fetched invoices:", allInvoices.map(inv => ({
        id: inv.id,
        status: inv.status,
        totalAmount: inv.totalAmount,
        dueAmount: inv.dueAmount
      })));

      setInvoices(allInvoices);
      calculateMetrics(allInvoices);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  const fetchClientSharedInvoices = async () => {
    if (!globalUser?.uid) return;

    setLoading(true);
    try {
      const sharedInvoicesRef = collection(db, "client", globalUser.uid, "sharedInvoices");
      const sharedInvoicesSnapshot = await getDocs(sharedInvoicesRef);

      const allInvoices = sharedInvoicesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setInvoices(allInvoices);
      calculateMetrics(allInvoices);
    } catch (error) {
      console.error("Error fetching shared invoices:", error);
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  const calculateMetrics = (invoicesList) => {
    // Total amount of all raised and paid invoices (both appear in raised tab)
    const raised = invoicesList
      .filter((inv) => inv.status === "raised" || inv.status === "paid")
      .reduce((sum, inv) => sum + (parseFloat(inv.totalAmount) || 0), 0);

    // Sum of all payments received (including from paid and partially paid invoices)
    const paid = invoicesList
      .reduce((sum, inv) => {
        if (inv.payments && inv.payments.length > 0) {
          const invoicePaid = inv.payments.reduce((pSum, payment) => {
            return pSum + (parseFloat(payment.amount) || 0) + (parseFloat(payment.tds) || 0);
          }, 0);
          return sum + invoicePaid;
        }
        return sum;
      }, 0);

    // Sum of remaining due amounts on raised and paid invoices
    const pending = invoicesList
      .filter((inv) => inv.status === "raised" || inv.status === "paid")
      .reduce((sum, inv) => sum + (parseFloat(inv.dueAmount) || 0), 0);

    // Total amount of scheduled invoices
    // const scheduled = invoicesList
    //   .filter((inv) => inv.status === "scheduled")
    //   .reduce((sum, inv) => sum + (parseFloat(inv.totalAmount) || 0), 0);

    setMetrics({ raised, paid, pending /*, scheduled */ });
  };

  const filterInvoicesByTab = () => {
    let filtered = [...invoices];

    console.log(`Filtering for tab: ${activeTab}, Total invoices: ${filtered.length}`);

    // Filter by status based on active tab
    if (activeTab === "raised") {
      // Raised tab shows both raised and paid invoices
      filtered = filtered.filter((inv) => inv.status === "raised" || inv.status === "paid");
    } else if (activeTab === "paid") {
      filtered = filtered.filter((inv) => inv.status === "paid");
    } else if (activeTab === "receivables") {
      // Receivables only shows invoices with pending amount > 0
      filtered = filtered.filter((inv) =>
        (inv.status === "raised" || inv.status === "paid") &&
        parseFloat(inv.dueAmount || inv.totalAmount) > 0
      );
    }
    // else if (activeTab === "scheduled") {
    //   filtered = filtered.filter((inv) => inv.status === "scheduled");
    // }

    console.log(`After status filter: ${filtered.length} invoices`);

    // Filter by date - only if invoice has the required date field
    if (dateFilter.startDate && dateFilter.endDate) {
      filtered = filtered.filter((inv) => {
        const dateValue = inv[dateFilter.type];
        if (!dateValue) return true; // Include if date field doesn't exist

        try {
          const invDate = new Date(dateValue);
          const startDate = new Date(dateFilter.startDate);
          const endDate = new Date(dateFilter.endDate);

          // Set time to start/end of day for accurate comparison
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(23, 59, 59, 999);
          invDate.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues

          return invDate >= startDate && invDate <= endDate;
        } catch (error) {
          console.error("Error filtering date:", error);
          return true; // Include if there's an error parsing date
        }
      });
    }

    console.log(`After date filter: ${filtered.length} invoices`);

    // Apply custom filters
    if (filters && filters.length > 0) {
      filtered = filtered.filter((inv) => {
        return filters.every((filter) => {
          const fieldValue = inv[filter.field];
          const filterValue = filter.value;

          if (!fieldValue) return false;

          switch (filter.operator) {
            case "equals":
              return String(fieldValue).toLowerCase() === String(filterValue).toLowerCase();
            case "contains":
              return String(fieldValue).toLowerCase().includes(String(filterValue).toLowerCase());
            case "greaterThan":
              return parseFloat(fieldValue) > parseFloat(filterValue);
            case "lessThan":
              return parseFloat(fieldValue) < parseFloat(filterValue);
            case "greaterThanOrEqual":
              return parseFloat(fieldValue) >= parseFloat(filterValue);
            case "lessThanOrEqual":
              return parseFloat(fieldValue) <= parseFloat(filterValue);
            default:
              return true;
          }
        });
      });
    }

    console.log(`After custom filters: ${filtered.length} invoices`);

    // Apply quick search filter
    if (quickSearchText.trim()) {
      const searchLower = quickSearchText.toLowerCase().trim();
      filtered = filtered.filter((inv) => {
        switch (activeQuickFilter) {
          case "caseNumber":
            return (inv.selectedCase?.caseNo || "").toLowerCase().includes(searchLower);
          case "petitioner":
            return (inv.selectedCase?.petitionerName || "").toLowerCase().includes(searchLower);
          case "respondent":
            return (inv.selectedCase?.respondentName || "").toLowerCase().includes(searchLower);
          case "court":
            return (inv.selectedCase?.courtName || "").toLowerCase().includes(searchLower);
          case "all":
          default:
            return (
              (inv.invoiceNumber || "").toLowerCase().includes(searchLower) ||
              (inv.contactName || "").toLowerCase().includes(searchLower) ||
              (inv.resourceName || "").toLowerCase().includes(searchLower) ||
              (inv.selectedCase?.caseNo || "").toLowerCase().includes(searchLower) ||
              (inv.selectedCase?.petitionerName || "").toLowerCase().includes(searchLower) ||
              (inv.selectedCase?.respondentName || "").toLowerCase().includes(searchLower) ||
              (inv.selectedCase?.courtName || "").toLowerCase().includes(searchLower)
            );
        }
      });
    }

    // Sort invoices based on sortConfig
    filtered.sort((a, b) => {
      let valueA = a[sortConfig.field];
      let valueB = b[sortConfig.field];

      // Handle date fields
      if (sortConfig.field === "raisedOn" || sortConfig.field === "dueDate" || sortConfig.field === "createdAt") {
        valueA = new Date(valueA || 0).getTime();
        valueB = new Date(valueB || 0).getTime();
      }
      // Handle numeric fields
      else if (sortConfig.field === "totalAmount" || sortConfig.field === "dueAmount") {
        valueA = parseFloat(valueA || 0);
        valueB = parseFloat(valueB || 0);
      }
      // Handle string fields
      else {
        valueA = String(valueA || "").toLowerCase();
        valueB = String(valueB || "").toLowerCase();
      }

      if (sortConfig.direction === "asc") {
        return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
      } else {
        return valueA < valueB ? 1 : valueA > valueB ? -1 : 0;
      }
    });

    setFilteredInvoices(filtered);
  };

  const incrementSeries = async () => {
    try {
      const settingsRef = doc(db, "lawyers", globalUser.uid, "settings", "invoiceSeries");

      let newSeries = currentSeries;

      console.log("=== Incrementing Invoice Series ===");
      console.log("Current series before increment:", currentSeries);

      // Check if current series is a pure number
      const numericValue = parseInt(currentSeries);
      if (!isNaN(numericValue) && currentSeries === numericValue.toString()) {
        // Pure number: 1 -> 2, 2 -> 3, etc.
        newSeries = (numericValue + 1).toString();
        console.log("Type: Pure number");
      } else {
        // Alphanumeric series: A1 -> A2, B10 -> B11, etc.
        // Extract the numeric part from the end
        const match = currentSeries.match(/^(.*?)(\d+)$/);
        if (match) {
          const prefix = match[1]; // e.g., "A", "INV", ""
          const number = parseInt(match[2]); // e.g., 1, 10, 100
          newSeries = prefix + (number + 1);
          console.log("Type: Alphanumeric");
          console.log("Prefix:", prefix, "Number:", number);
        } else {
          console.log("Type: No numeric part found, keeping as is");
        }
      }

      console.log("New series after increment:", newSeries);

      await setDoc(settingsRef, {
        currentSeries: newSeries,
        updatedAt: new Date().toISOString(),
      });
      setCurrentSeries(newSeries);
    } catch (error) {
      console.error("Error incrementing series:", error);
    }
  };

  const handleRaiseInvoice = async (invoiceData) => {
    try {
      const now = new Date().toISOString();
      const newInvoice = {
        ...invoiceData,
        userId: globalUser.uid,
        raisedBy: userName,
        raisedOn: now,
        status: "raised",
        createdAt: now,
        timeline: [
          {
            id: Date.now().toString(),
            type: "invoice_created",
            timestamp: now,
            by: userName,
            changes: {
              "Invoice Number": {
                old: "-",
                new: invoiceData.invoiceNumber
              },
              "Total Amount": {
                old: "-",
                new: `${invoiceData.currency === "INR" ? "₹" : invoiceData.currency === "USD" ? "$" : "€"} ${invoiceData.totalAmount}`
              },
              "Invoice Status": {
                old: "-",
                new: "raised"
              }
            }
          }
        ]
      };

      const invoiceDocRef = await addDoc(collection(db, "lawyers", globalUser.uid, "invoices"), newInvoice);

      // Sync invoice to client's portal if they have a registered account
      if (invoiceData.contactId) {
        await syncInvoiceToClient(
          invoiceData.contactId,
          newInvoice,
          invoiceDocRef.id,
          globalUser.uid,
          userName
        );
      }

      toast.success("Invoice raised successfully");

      // Auto-increment series if it's numeric
      await incrementSeries();

      fetchAllInvoices();
      setIsFormOpen(false);
    } catch (error) {
      console.error("Error raising invoice:", error);
      toast.error("Failed to raise invoice");
    }
  };

  // const handleScheduleInvoice = async (invoiceData) => {
  //   try {
  //     const newInvoice = {
  //       ...invoiceData,
  //       userId: globalUser.uid,
  //       raisedBy: userName,
  //       status: "scheduled",
  //       createdAt: new Date().toISOString(),
  //     };

  //     await addDoc(collection(db, "lawyers", globalUser.uid, "invoices"), newInvoice);

  //     toast.success("Invoice scheduled successfully");

  //     // Auto-increment series if it's numeric
  //     await incrementSeries();

  //     fetchAllInvoices();
  //     setIsScheduleFormOpen(false);
  //   } catch (error) {
  //     console.error("Error scheduling invoice:", error);
  //     toast.error("Failed to schedule invoice");
  //   }
  // };

  const handleDeleteInvoice = async (invoiceId) => {
    try {
      // Find the invoice to get contactId before deleting
      const invoiceToDelete = invoices.find((inv) => inv.id === invoiceId);

      await deleteDoc(doc(db, "lawyers", globalUser.uid, "invoices", invoiceId));

      // Also delete synced invoice from client portal
      if (invoiceToDelete?.contactId) {
        await deleteSyncedInvoice(invoiceToDelete.contactId, invoiceId);
      }

      toast.success("Invoice deleted successfully");
      fetchAllInvoices();
    } catch (error) {
      console.error("Error deleting invoice:", error);
      toast.error("Failed to delete invoice");
    }
  };

  const handleEditInvoice = (invoice) => {
    setEditingInvoice(invoice);
    setIsEditMode(true);
    setIsFormOpen(true);
  };

  const handleUpdateInvoice = async (invoiceData) => {
    if (!editingInvoice) return;

    try {
      const invoiceRef = doc(db, "lawyers", globalUser.uid, "invoices", editingInvoice.id);
      const now = new Date().toISOString();

      // Build timeline event tracking the changes
      const changes = {};

      // Track invoice number changes
      if (editingInvoice.invoiceNumber !== invoiceData.invoiceNumber) {
        changes["Invoice Number"] = {
          old: editingInvoice.invoiceNumber,
          new: invoiceData.invoiceNumber
        };
      }

      // Track contact changes
      if (editingInvoice.contactName !== invoiceData.contactName) {
        changes["Contact"] = {
          old: editingInvoice.contactName,
          new: invoiceData.contactName
        };
      }

      // Track due date changes
      if (editingInvoice.dueDate !== invoiceData.dueDate) {
        changes["Due Date"] = {
          old: editingInvoice.dueDate,
          new: invoiceData.dueDate
        };
      }

      // Track amount changes
      if (editingInvoice.totalAmount !== invoiceData.totalAmount) {
        const currencySymbol = invoiceData.currency === "INR" ? "₹" : invoiceData.currency === "USD" ? "$" : "€";
        changes["Total Amount"] = {
          old: `${currencySymbol} ${editingInvoice.totalAmount}`,
          new: `${currencySymbol} ${invoiceData.totalAmount}`
        };
      }

      // Track currency changes
      if (editingInvoice.currency !== invoiceData.currency) {
        changes["Currency"] = {
          old: editingInvoice.currency,
          new: invoiceData.currency
        };
      }

      // Create timeline event if there were changes
      const timelineEvent = Object.keys(changes).length > 0 ? {
        id: Date.now().toString(),
        type: "invoice_updated",
        timestamp: now,
        by: userName,
        changes: changes
      } : null;

      const updatedInvoice = {
        ...invoiceData,
        userId: globalUser.uid,
        raisedBy: editingInvoice.raisedBy, // Keep original raiser
        raisedOn: editingInvoice.raisedOn, // Keep original raise date
        status: editingInvoice.status, // Keep current status
        payments: editingInvoice.payments || [], // Keep existing payments
        creditNotes: editingInvoice.creditNotes || [], // Keep existing credit notes
        notes: editingInvoice.notes || [], // Keep existing notes
        timeline: timelineEvent
          ? [...(editingInvoice.timeline || []), timelineEvent]
          : (editingInvoice.timeline || []),
        updatedAt: now,
      };

      await updateDoc(invoiceRef, updatedInvoice);

      // Sync updated invoice to client's portal
      if (invoiceData.contactId || editingInvoice.contactId) {
        await syncInvoiceToClient(
          invoiceData.contactId || editingInvoice.contactId,
          updatedInvoice,
          editingInvoice.id,
          globalUser.uid,
          userName
        );
      }

      toast.success("Invoice updated successfully");
      fetchAllInvoices();
      setIsFormOpen(false);
      setIsEditMode(false);
      setEditingInvoice(null);
    } catch (error) {
      console.error("Error updating invoice:", error);
      toast.error("Failed to update invoice");
    }
  };

  const handleExportInvoices = () => {
    if (filteredInvoices.length === 0) {
      toast.error("No invoices to export");
      return;
    }

    try {
      const headers = ["Invoice No", "Date", "Client", "Type", "Total", "Due", "Status"];
      const csvContent = [
        headers.join(","),
        ...filteredInvoices.map((inv) =>
          [
            inv.invoiceNumber,
            inv.raisedOn,
            `"${inv.contactName || ""}"`,
            inv.resourceType === "case" ? "Case" : "Custom",
            inv.totalAmount,
            inv.dueAmount || inv.totalAmount,
            inv.status,
          ].join(",")
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `invoices_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Invoices exported successfully");
    } catch (error) {
      console.error("Error exporting invoices:", error);
      toast.error("Failed to export invoices");
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  // Derive a display status for the ledger table. Real stored statuses are
  // draft | sent | raised | paid; Pending/Overdue are derived from due date.
  const todayMs = new Date().setHours(0, 0, 0, 0);
  const displayStatus = (inv) => {
    if (inv.status === "paid") return "paid";
    if (inv.status === "draft") return "draft";
    const due = inv.dueDate ? new Date(inv.dueDate).getTime() : null;
    if (due && due < todayMs) return "overdue";
    if (inv.status === "sent") return "sent";
    return "pending";
  };
  const STATUS_PILL = {
    paid: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    sent: "bg-blue-50 text-blue-700 ring-blue-200",
    pending: "bg-amber-50 text-amber-700 ring-amber-200",
    overdue: "bg-red-50 text-red-700 ring-red-200",
    draft: "bg-slate-100 text-slate-600 ring-slate-200",
  };
  const fmtInvDate = (s) =>
    s ? new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  // Ledger stat cards (count + amount), computed over ALL invoices.
  const ledger = invoices.reduce(
    (acc, inv) => {
      const amt = parseFloat(inv.totalAmount) || 0;
      acc.total.count += 1;
      acc.total.amt += amt;
      const ds = displayStatus(inv);
      if (ds === "paid") { acc.paid.count += 1; acc.paid.amt += amt; }
      else if (ds === "overdue") { acc.overdue.count += 1; acc.overdue.amt += (parseFloat(inv.dueAmount) || amt); }
      else if (ds !== "draft") { acc.pending.count += 1; acc.pending.amt += (parseFloat(inv.dueAmount) || amt); }
      return acc;
    },
    { total: { count: 0, amt: 0 }, paid: { count: 0, amt: 0 }, pending: { count: 0, amt: 0 }, overdue: { count: 0, amt: 0 } }
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <div className="flex items-center justify-center py-24">
          <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-yellow-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative bg-[#F8FAFC] text-gray-900">
      <main className="px-4 sm:px-6 lg:px-8 py-4">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
              Invoices
            </h1>
            <p className="mt-0.5 text-xs text-slate-500">
              Create and manage client invoices
            </p>
          </div>
          {!isClient && (
            <div className="flex shrink-0 items-center gap-2">
              <button
                onClick={() => setIsSeriesDialogOpen(true)}
                className={ACTION_BTN_WHITE}
              >
                Invoice Series
              </button>
              <button
                onClick={() => navigate("/portal/create-invoice")}
                className={ACTION_BTN_GOLD}
              >
                <Plus className={ACTION_BTN_ICON} />
                Raise Invoice
              </button>
            </div>
          )}
        </div>

        {/* Ledger stat cards */}
        <div className="mt-4 grid grid-cols-2 gap-2.5 lg:grid-cols-4">
          {[
            { label: "Total Invoices", count: ledger.total.count, amt: ledger.total.amt, Icon: FileText, tint: "bg-blue-50 text-blue-600", num: "text-slate-900" },
            { label: "Paid", count: ledger.paid.count, amt: ledger.paid.amt, Icon: CheckCircle2, tint: "bg-emerald-50 text-emerald-600", num: "text-emerald-600" },
            { label: "Pending", count: ledger.pending.count, amt: ledger.pending.amt, Icon: Clock, tint: "bg-amber-50 text-amber-600", num: "text-amber-600" },
            { label: "Overdue", count: ledger.overdue.count, amt: ledger.overdue.amt, Icon: TrendingUp, tint: "bg-red-50 text-red-600", num: "text-red-600" },
          ].map((c) => (
            <div key={c.label} className="flex items-start justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
              <div className="min-w-0">
                <p className="truncate text-[11px] font-medium text-slate-500">{c.label}</p>
                <p className={`text-xl font-bold leading-tight ${c.num}`}>{c.count}</p>
                <p className="mt-0.5 text-xs font-medium text-slate-500">{formatAmount(c.amt)}</p>
              </div>
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${c.tint}`}>
                <c.Icon className="h-4 w-4" />
              </span>
            </div>
          ))}
        </div>

        <div className="relative mt-4">
          {/* Tabs */}
          <div className="mb-4 flex gap-6 border-b border-slate-200">
            {["raised", "paid", "receivables"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`relative pb-3 text-sm font-semibold capitalize transition-colors ${
                  activeTab === tab
                    ? "text-slate-900"
                    : "text-slate-500 hover:text-slate-800"
                }`}
              >
                {tab}
                {activeTab === tab && (
                  <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-yellow-500" />
                )}
              </button>
            ))}
          </div>

          {/* Date filter + actions */}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-slate-500">Date:</span>
              <SelectMenu
                value={dateFilter.type}
                onChange={(e) => setDateFilter({ ...dateFilter, type: e.target.value })}
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 shadow-sm [color-scheme:light] focus:border-yellow-400 focus:outline-none"
              >
                <option value="raisedOn">Raised On</option>
                <option value="dueDate">Due Date</option>
              </SelectMenu>
              <input
                type="date"
                value={dateFilter.startDate}
                onChange={(e) => setDateFilter({ ...dateFilter, startDate: e.target.value })}
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 shadow-sm [color-scheme:light] focus:border-yellow-400 focus:outline-none"
              />
              <span className="text-sm text-slate-500">to</span>
              <input
                type="date"
                value={dateFilter.endDate}
                onChange={(e) => setDateFilter({ ...dateFilter, endDate: e.target.value })}
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 shadow-sm [color-scheme:light] focus:border-yellow-400 focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowSortModal(true)} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 shadow-sm transition-colors hover:bg-slate-50">Sort</button>
              <button onClick={() => setShowFilterModal(true)} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 shadow-sm transition-colors hover:bg-slate-50">Filter</button>
              <button onClick={handleExportInvoices} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 shadow-sm transition-colors hover:bg-slate-50">Export</button>
            </div>
          </div>

          {/* Quick search + filters */}
          <div className="mb-4 space-y-2">
            <div className="relative w-full max-w-md">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder={
                  activeQuickFilter === "all"
                    ? "Search invoices..."
                    : activeQuickFilter === "caseNumber"
                    ? "Search by case number..."
                    : activeQuickFilter === "petitioner"
                    ? "Search by petitioner name..."
                    : activeQuickFilter === "respondent"
                    ? "Search by respondent name..."
                    : "Search by court name..."
                }
                value={quickSearchText}
                onChange={(e) => setQuickSearchText(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-9 pr-9 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
              />
              {quickSearchText && (
                <button
                  onClick={() => setQuickSearchText("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-700"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="mr-1 text-xs text-slate-500">Search by:</span>
              {[
                { key: "all", label: "All" },
                { key: "caseNumber", label: "Case Number" },
                { key: "petitioner", label: "Petitioner" },
                { key: "respondent", label: "Respondent" },
                { key: "court", label: "Court Name" },
              ].map((filter) => (
                <button
                  key={filter.key}
                  onClick={() => setActiveQuickFilter(filter.key)}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    activeQuickFilter === filter.key
                      ? "bg-yellow-100 text-yellow-800 ring-1 ring-inset ring-yellow-300"
                      : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          {/* Invoice ledger table */}
          <InvoiceList
            invoices={filteredInvoices}
            onDelete={handleDeleteInvoice}
            formatAmount={formatAmount}
            globalUser={globalUser}
            onRefresh={isClient ? fetchClientSharedInvoices : fetchAllInvoices}
            onEdit={handleEditInvoice}
            readOnly={isClient}
          />
        </div>
      </main>

      {/* Raise Invoice Modal */}
      <RaiseInvoiceForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setIsEditMode(false);
          setEditingInvoice(null);
        }}
        onSubmit={isEditMode ? handleUpdateInvoice : handleRaiseInvoice}
        cases={cases}
        clients={clients}
        isSchedule={false}
        currentSeries={currentSeries}
        isEditMode={isEditMode}
        editData={editingInvoice}
      />

      {/* Schedule Invoice Modal */}
      {/* <RaiseInvoiceForm
        isOpen={isScheduleFormOpen}
        onClose={() => setIsScheduleFormOpen(false)}
        onSubmit={handleScheduleInvoice}
        cases={cases}
        clients={clients}
        isSchedule={true}
        currentSeries={currentSeries}
      /> */}

      {/* Invoice Series Dialog */}
      <InvoiceSeriesDialog
        isOpen={isSeriesDialogOpen}
        onClose={() => setIsSeriesDialogOpen(false)}
        globalUser={globalUser}
        onUpdate={(newSeries) => {
          setCurrentSeries(newSeries);
        }}
      />

      {/* Sort Modal */}
      {showSortModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1E1E45] rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-yellow-500">Sort Invoices</h2>
              <button
                onClick={() => setShowSortModal(false)}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-gray-400 mb-2">Sort On</label>
                <SelectMenu
                  value={sortConfig.field}
                  onChange={(e) => setSortConfig({ ...sortConfig, field: e.target.value })}
                  className="w-full bg-[#0f0f23] border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#C99F4A]"
                >
                  <optgroup label="Recently Used">
                    <option value="raisedOn">Raised On</option>
                    <option value="dueDate">Due On</option>
                  </optgroup>
                  <optgroup label="Static Fields">
                    <option value="totalAmount">Total Amount</option>
                    <option value="dueAmount">Due Amount</option>
                    <option value="invoiceNumber">Invoice Number</option>
                    <option value="contactName">Contact</option>
                    <option value="resourceName">Project/Case</option>
                  </optgroup>
                </SelectMenu>
              </div>

              <div>
                <label className="block text-gray-400 mb-3">Sort Direction</label>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="sortDirection"
                      value="asc"
                      checked={sortConfig.direction === "asc"}
                      onChange={(e) => setSortConfig({ ...sortConfig, direction: e.target.value })}
                      className="w-4 h-4 text-[#C99F4A] focus:ring-[#C99F4A]"
                    />
                    <span className="text-white">Ascending</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="sortDirection"
                      value="desc"
                      checked={sortConfig.direction === "desc"}
                      onChange={(e) => setSortConfig({ ...sortConfig, direction: e.target.value })}
                      className="w-4 h-4 text-[#C99F4A] focus:ring-[#C99F4A]"
                    />
                    <span className="text-white">Descending</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setSortConfig({ field: "raisedOn", direction: "desc" });
                }}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Clear
              </button>
              <button
                onClick={() => setShowSortModal(false)}
                className="px-4 py-2 bg-[#C99F4A] hover:bg-[#D19B15] text-[#0f0f23] rounded-lg font-medium transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1E1E45] rounded-lg p-6 w-full max-w-3xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-yellow-500">Filter Invoices</h2>
              <button
                onClick={() => setShowFilterModal(false)}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              {filters.map((filter, index) => (
                <div key={index} className="flex gap-3 items-start">
                  <button
                    onClick={() => {
                      const newFilters = filters.filter((_, i) => i !== index);
                      setFilters(newFilters);
                    }}
                    className="text-gray-400 hover:text-red-500 mt-2"
                  >
                    ×
                  </button>
                  <div className="flex-1 grid grid-cols-3 gap-3">
                    <SelectMenu
                      value={filter.field}
                      onChange={(e) => {
                        const newFilters = [...filters];
                        newFilters[index].field = e.target.value;
                        setFilters(newFilters);
                      }}
                      className="bg-[#0f0f23] border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#C99F4A]"
                    >
                      <option value="">Select Field</option>
                      <optgroup label="Static Fields">
                        <option value="contactName">Contact</option>
                        <option value="invoiceNumber">Invoice Number</option>
                        <option value="resourceName">Project/Case</option>
                        <option value="totalAmount">Total Amount</option>
                        <option value="dueAmount">Due Amount</option>
                        <option value="raisedOn">Raised On</option>
                        <option value="dueDate">Due Date</option>
                      </optgroup>
                    </SelectMenu>
                    <SelectMenu
                      value={filter.operator}
                      onChange={(e) => {
                        const newFilters = [...filters];
                        newFilters[index].operator = e.target.value;
                        setFilters(newFilters);
                      }}
                      className="bg-[#0f0f23] border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#C99F4A]"
                    >
                      <option value="equals">Equals</option>
                      <option value="contains">Contains</option>
                      <option value="greaterThan">Greater Than</option>
                      <option value="lessThan">Less Than</option>
                      <option value="greaterThanOrEqual">Greater Than or Equal</option>
                      <option value="lessThanOrEqual">Less Than or Equal</option>
                    </SelectMenu>
                    <input
                      type="text"
                      value={filter.value}
                      onChange={(e) => {
                        const newFilters = [...filters];
                        newFilters[index].value = e.target.value;
                        setFilters(newFilters);
                      }}
                      placeholder="Value"
                      className="bg-[#0f0f23] border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#C99F4A]"
                    />
                  </div>
                  {index < filters.length - 1 && (
                    <span className="text-gray-400 mt-2 text-sm">AND</span>
                  )}
                </div>
              ))}

              <button
                onClick={() => {
                  setFilters([...filters, { field: "", operator: "equals", value: "" }]);
                }}
                className="text-blue-400 hover:text-blue-300 flex items-center gap-2"
              >
                + Add Filter
              </button>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setFilters([]);
                }}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Clear
              </button>
              <button
                onClick={() => setShowFilterModal(false)}
                className="px-4 py-2 bg-[#C99F4A] hover:bg-[#D19B15] text-[#0f0f23] rounded-lg font-medium transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Invoices;
